import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2 } from 'lucide-react';
import axios from 'axios';
import SEO from '../components/SEO';
import AppShell from '../components/AppShell';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Band styling — earthy, honest, non-judgmental.
const BAND_STYLE = {
  strong:          { dot: 'bg-forest',       chip: 'bg-forest/10 text-forest border-forest/30' },
  steady:          { dot: 'bg-olive',        chip: 'bg-olive/10 text-olive border-olive/30' },
  mixed:           { dot: 'bg-sand',         chip: 'bg-sand/40 text-charcoal border-line-strong' },
  worth_watching:  { dot: 'bg-clay',         chip: 'bg-clay/10 text-clay border-clay/30' },
  needs_attention: { dot: 'bg-clay-soft',    chip: 'bg-clay/15 text-clay border-clay/50' },
};

const DomainCard = ({ d, testid }) => {
  const style = BAND_STYLE[d.band] || BAND_STYLE.mixed;
  return (
    <div className="card hover:border-forest/30" data-testid={testid}>
      <div className="flex items-center justify-between mb-3">
        <span className="eyebrow">{d.eyebrow}</span>
        <span className={`inline-flex items-center gap-1.5 text-[11px] font-mono uppercase tracking-widest px-2 py-1 rounded-full border ${style.chip}`}>
          <span className={`w-1.5 h-1.5 rounded-full ${style.dot}`} />
          {d.band_label}
        </span>
      </div>
      <h3
        className="font-display text-xl text-forest"
        style={{ fontVariationSettings: "'opsz' 36, 'SOFT' 40, 'wght' 460" }}
      >
        {d.title}
      </h3>
    </div>
  );
};

const LifeProfile = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [data, setData] = useState(
    location.state?.profile ? {
      profile: location.state.profile,
      assessment_id: location.state.assessment_id,
      created_at: null,
    } : null
  );
  const [loading, setLoading] = useState(!location.state?.profile);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    if (data) return;
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/assessment/latest`);
        if (cancelled) return;
        if (!r.data.assessment) {
          navigate('/assess', { replace: true });
          return;
        }
        setData({
          profile: r.data.assessment.profile,
          assessment_id: r.data.assessment.assessment_id,
          created_at: r.data.assessment.created_at,
        });
      } catch {
        navigate('/assess', { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [data, navigate]);

  // Reveal animation — 900ms curtain, then everything fades up.
  useEffect(() => {
    if (!data) return;
    const t = setTimeout(() => setRevealed(true), 750);
    return () => clearTimeout(t);
  }, [data]);

  if (loading || !data) {
    return (
      <AppShell>
        <div className="max-w-2xl mx-auto px-6 py-32 text-center">
          <Loader2 className="w-6 h-6 text-forest animate-spin mx-auto" strokeWidth={1.5} />
        </div>
      </AppShell>
    );
  }

  const { profile } = data;

  return (
    <AppShell>
      <SEO title="Your Life Profile" description="Your Summaros Life Profile: a private picture of the parts of your life." path="/life-profile" noindex />

      {/* ── Reveal moment ───────────────────────────── */}
      <section className={`relative overflow-hidden transition-colors duration-1000 ${revealed ? 'bg-cream' : 'bg-forest'}`}>
        <div className="max-w-3xl mx-auto px-6 pt-20 sm:pt-28 pb-14 text-center min-h-[42vh]">
          <p className={`eyebrow mb-6 transition-colors duration-700 ${revealed ? 'text-ink-muted' : 'text-cream/60'}`}>
            Your Life Profile
          </p>
          <h1
            className={`display-hero text-4xl sm:text-6xl leading-[1.06] mb-6 text-balance transition-colors duration-1000 ${revealed ? 'text-forest' : 'text-cream'}`}
            data-testid="life-profile-title"
            style={{ fontVariationSettings: "'opsz' 144, 'SOFT' 60, 'wght' 340" }}
          >
            This is you. Roughly.
          </h1>
          <p className={`text-[17px] sm:text-[19px] leading-[1.6] max-w-xl mx-auto transition-colors duration-1000 ${revealed ? 'text-ink-muted' : 'text-cream/70'}`}>
            Not really a number. More like a sketch of the parts of your life, how they're going, and where they seem to nudge each other.
          </p>
        </div>
      </section>

      {/* ── Domains grid ────────────────────────────── */}
      <section className={`max-w-5xl mx-auto px-6 py-12 sm:py-16 transition-opacity duration-1000 ${revealed ? 'opacity-100' : 'opacity-0'}`}>
        <h2 className="eyebrow mb-6">The picture</h2>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="life-profile-grid">
          {profile.domains.map((d) => <DomainCard key={d.id} d={d} testid={`domain-${d.id}`} />)}
        </div>
      </section>

      {/* ── Looking good / Worth a look ─────────────── */}
      <section className={`max-w-5xl mx-auto px-6 py-8 sm:py-12 grid md:grid-cols-2 gap-6 transition-opacity duration-1000 ${revealed ? 'opacity-100' : 'opacity-0'}`}>
        <div className="rounded-2xl border border-line bg-cream-soft p-7" data-testid="looking-good">
          <p className="eyebrow mb-3 text-forest">Looking good</p>
          <h3 className="display-serif text-2xl text-forest mb-5" style={{ fontVariationSettings: "'opsz' 48, 'SOFT' 40, 'wght' 420" }}>
            You're doing well here.
          </h3>
          {profile.looking_good.length === 0 ? (
            <p className="text-[15px] text-ink-muted">Nothing obvious yet. That's okay.</p>
          ) : (
            <ul className="space-y-3">
              {profile.looking_good.map((d) => (
                <li key={d.id} className="flex items-baseline gap-3">
                  <span className={`w-1.5 h-1.5 rounded-full ${BAND_STYLE[d.band].dot} translate-y-1`} />
                  <span className="text-[15px] text-ink">
                    <span className="font-medium text-forest">{d.title}</span>
                    <span className="text-ink-muted">, {d.band_label.toLowerCase()}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="rounded-2xl border border-line bg-cream-soft p-7" data-testid="worth-a-look">
          <p className="eyebrow mb-3 text-clay">Worth a look</p>
          <h3 className="display-serif text-2xl text-forest mb-5" style={{ fontVariationSettings: "'opsz' 48, 'SOFT' 40, 'wght' 420" }}>
            These may deserve attention.
          </h3>
          {profile.worth_a_look.length === 0 ? (
            <p className="text-[15px] text-ink-muted">Nothing loud right now.</p>
          ) : (
            <ul className="space-y-3">
              {profile.worth_a_look.map((d) => (
                <li key={d.id} className="flex items-baseline gap-3">
                  <span className={`w-1.5 h-1.5 rounded-full ${BAND_STYLE[d.band].dot} translate-y-1`} />
                  <span className="text-[15px] text-ink">
                    <span className="font-medium text-forest">{d.title}</span>
                    <span className="text-ink-muted">, {d.band_label.toLowerCase()}</span>
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      {/* ── Connections ─────────────────────────────── */}
      {profile.connections.length > 0 && (
        <section className={`max-w-5xl mx-auto px-6 py-8 sm:py-12 transition-opacity duration-1000 ${revealed ? 'opacity-100' : 'opacity-0'}`}>
          <p className="eyebrow mb-3">Connections</p>
          <h3 className="display-serif text-2xl sm:text-3xl text-forest mb-8" style={{ fontVariationSettings: "'opsz' 64, 'SOFT' 40, 'wght' 400" }}>
            Some parts of your life may be talking to each other.
          </h3>
          <div className="grid md:grid-cols-2 gap-4" data-testid="connections">
            {profile.connections.map((c, i) => (
              <div key={i} className="card border-clay/30">
                <div className="flex items-center gap-2 text-[13px] font-mono uppercase tracking-widest text-clay mb-3">
                  <span>{c.left.title}</span>
                  <span>↔</span>
                  <span>{c.right.title}</span>
                </div>
                <p className="text-[16px] leading-relaxed text-ink text-pretty">{c.text}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Start here ──────────────────────────────── */}
      <section className={`bg-forest text-cream transition-opacity duration-1000 ${revealed ? 'opacity-100' : 'opacity-0'}`}>
        <div className="max-w-3xl mx-auto px-6 py-20 sm:py-24">
          <p className="eyebrow text-cream/60 mb-4">Start here</p>
          <h3
            className="display-serif text-3xl sm:text-4xl text-cream mb-10"
            style={{ fontVariationSettings: "'opsz' 72, 'SOFT' 50, 'wght' 380" }}
          >
            Small things you could try this week.
          </h3>
          <ol className="space-y-6" data-testid="start-here">
            {profile.start_here.map((s, i) => (
              <li key={i} className="flex gap-5">
                <span className="font-mono text-xs text-cream/50 pt-1 shrink-0">0{i + 1}</span>
                <div>
                  <p className="text-[13px] font-mono uppercase tracking-widest text-cream/60 mb-1.5">{s.domain_title}</p>
                  <p className="text-[17px] sm:text-[19px] leading-[1.55] text-cream/95 text-pretty">{s.action}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="mt-12 pt-8 border-t border-cream/15 flex flex-wrap gap-3">
            <Link to="/my-life" className="inline-flex items-center gap-2 bg-cream text-forest px-6 py-3.5 rounded-full font-medium hover:bg-cream-soft transition-colors" data-testid="profile-cta-my-life">
              Go to My Life <ArrowRight className="w-4 h-4" strokeWidth={2} />
            </Link>
            <Link to="/timeline" className="inline-flex items-center gap-2 border border-cream/30 text-cream px-6 py-3.5 rounded-full font-medium hover:bg-cream/10 transition-colors" data-testid="profile-cta-timeline">
              Add to Timeline
            </Link>
          </div>
        </div>
      </section>
    </AppShell>
  );
};

export default LifeProfile;
